"use client";

import { HydrateAtoms, type OnlyWritableAtomRecord } from "../HydrateAtoms";
import type { AtomStoreProps } from "../types";
import { useCreation } from "ahooks";
import React, { useEffect, useMemo } from "react";
import {
  createContext,
  useContext,
  useContextSelector,
} from "use-context-selector";
import type { Class, OmitByValueExact } from "utility-types";
import { v4 as uuid } from "uuid";

const getFullyQualifiedScope = (storeName: string, scope: string) => {
  return `${storeName}:${scope}`;
};

const CURRENT_SCOPE = "$current";
const AtomStoreContext = createContext(new Map<string, { store: any }>());

export const useAtomStore = <T,>(
  storeName: string,
  scope: string = CURRENT_SCOPE
): T => {
  const item = useContextSelector(AtomStoreContext, (storeContext) => {
    return storeContext.get(getFullyQualifiedScope(storeName, scope));
  });

  if (!item) {
    throw new Error(`No store found for ${storeName} with scope ${scope}`);
  }

  const { store } = item;

  return store;
};

export const useOptionalAtomStore = <T,>(
  storeName: string,
  scope: string = CURRENT_SCOPE
): T | undefined => {
  const item = useContextSelector(AtomStoreContext, (storeContext) => {
    return storeContext.get(getFullyQualifiedScope(storeName, scope));
  });

  return item?.store;
};

export type ProviderStoreProp<T extends object> = T | (() => T);

export type ProviderProps<T extends object> = OmitByValueExact<
  {
    scope?: string;
    store?: ProviderStoreProp<T>;
    values?: AtomStoreProps<T>;
    children: React.ReactNode | ((store: T) => React.ReactNode);
  },
  never
>;

export const createAtomProvider = <
  N extends string,
  T extends object,
>(options: {
  storeName: N;
  StoreClass?: Class<T>;
  effect?: React.FC;
  onCreate?: (data: { store: T; scope: string }) => void;
  onRemove?: (data: { store: T; scope: string }) => void;
}) => {
  const { storeName, StoreClass, effect: Effect, onCreate, onRemove } = options;

  const StoreLifecycle = ({
    store,
    scope,
  }: {
    store: T;
    scope?: string;
    onCreate?: (data: { store: T; scope: string }) => void;
    onRemove?: (data: { store: T; scope: string }) => void;
  }) => {
    useEffect(() => {
      if (!scope) return;

      onCreate?.({
        store,
        scope,
      });
      return () => {
        onRemove?.({
          store,
          scope,
        });
      };
    }, [store, scope]);

    return null;
  };

  const Provider = (props: ProviderProps<T>) => {
    const { scope, children } = props;
    const storeProp = (props as any).store as ProviderStoreProp<T> | undefined;
    const valuesProp = (props as any).values as AtomStoreProps<T> | undefined;
    const values = useMemo(
      () => valuesProp ?? ({} as AtomStoreProps<T>),
      [valuesProp]
    );

    const parentStoreContext = useContext(AtomStoreContext);

    const storeDep = typeof storeProp === "function" ? undefined : storeProp;

    const store = useCreation(() => {
      if (typeof storeProp === "function") {
        return storeProp();
      }

      if (storeProp) {
        return storeProp;
      }

      if (!StoreClass) {
        throw new Error("StoreClass is required when store is not provided");
      }

      const newStore = new StoreClass();

      return newStore;
    }, [StoreClass, scope, storeDep]);

    const storeContext = React.useMemo(() => {
      const newStoreContext = new Map(parentStoreContext);

      if (scope) {
        newStoreContext.set(getFullyQualifiedScope(storeName, scope), {
          store,
        });
      }

      newStoreContext.set(getFullyQualifiedScope(storeName, CURRENT_SCOPE), {
        store,
      });

      return newStoreContext;
    }, [store, parentStoreContext]);

    const atoms = ((store as any).props ?? {}) as OnlyWritableAtomRecord<any>;

    const key = useCreation(() => uuid(), [store]);

    return (
      <AtomStoreContext.Provider key={key} value={storeContext}>
        <StoreLifecycle store={store} scope={scope} />
        <HydrateAtoms atoms={atoms} values={values}>
          {!!Effect && <Effect />}

          {typeof children === "function" ? children(store) : children}
        </HydrateAtoms>
      </AtomStoreContext.Provider>
    );
  };
  Provider.displayName = `${storeName}AtomProvider`;

  return Provider;
};
